<script>
import { userService } from '@/shared/services/user.service';
import notificationService from '@/shared/services/notification.service';

/**
 * La confirmation avant l'envoi d'un mot de passe temporaire.
 *
 * L'action est irréversible du point de vue de son destinataire — elle coupe
 * l'accès qu'il avait — et elle part par courriel : elle mérite le même arrêt
 * que la suppression d'un compte.
 *
 * La mise en page est celle des fenêtres récentes de l'application
 * (`.modal-footer-actions`, `.modal-btn`, corps à 13px) et non celle des
 * fenêtres de suppression, restées sur les boutons Bootstrap d'origine : une
 * fenêtre ouverte depuis la liste des comptes doit ressembler à « Importer des
 * utilisateurs », qui s'ouvre du même écran.
 *
 * Le mot de passe n'est jamais affiché ici : l'API ne le renvoie pas, et il
 * n'a rien à faire sur l'écran de celui qui l'envoie.
 */
export default {
  name: 'TemporaryPasswordModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:modelValue', 'sent'],
  data() {
    return {
      sending: false,
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },

    fullName() {
      return (
        [this.user?.firstName, this.user?.lastName].filter(Boolean).join(' ') ||
        this.user?.email ||
        ''
      );
    },

    /** Les initiales tiennent lieu de portrait: aucun compte n'en porte un. */
    initials() {
      const letters = [this.user?.firstName, this.user?.lastName]
        .filter(Boolean)
        .map((part) => part.trim().charAt(0))
        .join('');

      return (letters || this.user?.email?.charAt(0) || '?').toUpperCase();
    },
  },
  methods: {
    handleCancel() {
      this.isVisible = false;
    },

    onClose() {
      this.$emit('update:modelValue', false);
    },

    async handleConfirm() {
      if (!this.user?.id) return;

      this.sending = true;

      try {
        await userService.sendTemporaryPassword(this.user.id);

        this.isVisible = false;
        notificationService.toastSuccess(
          `Un mot de passe temporaire a été envoyé à ${this.user.email}.`,
        );

        this.$emit('sent', this.user.id);
      } catch (error) {
        notificationService.toastError(
          error.response?.data?.message ||
            "Le mot de passe temporaire n'a pas pu être envoyé.",
        );
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<template>
  <BModal
    v-model="isVisible"
    modal-class="zoomIn"
    hide-footer
    no-close-on-backdrop
    centered
    title="Envoyer un mot de passe temporaire"
    @hidden="onClose"
  >
    <p class="temp-password__lead">
      Un mot de passe temporaire sera envoyé par e-mail. La plateforme demandera
      à cette personne d’en choisir un nouveau dès sa prochaine connexion.
    </p>

    <!-- Qui reçoit, nommé et adressé: c'est la ligne qu'on relit avant de
         cliquer, et l'adresse est ce qui distingue deux homonymes. -->
    <div class="temp-password__target">
      <span class="temp-password__initials" aria-hidden="true">
        {{ initials }}
      </span>
      <span class="temp-password__identity">
        <span class="temp-password__name">{{ fullName }}</span>
        <span class="temp-password__email">{{ user?.email }}</span>
      </span>
    </div>

    <!-- Ce que l'action coûte, dit avant le clic: le mot de passe actuel
         cesse de fonctionner, y compris si la personne s'en sert à l'instant. -->
    <p class="temp-password__notice">
      <i class="ri-error-warning-line" aria-hidden="true"></i>
      <span>
        Son mot de passe actuel cessera immédiatement de fonctionner, et son
        compte n’ouvrira plus rien d’autre que le changement de mot de passe.
      </span>
    </p>

    <footer class="modal-footer-actions">
      <button
        type="button"
        class="modal-btn modal-btn--ghost"
        :disabled="sending"
        @click="handleCancel"
      >
        Annuler
      </button>
      <button
        type="button"
        class="modal-btn modal-btn--primary"
        :disabled="sending"
        data-testid="temporary-password-confirm"
        @click="handleConfirm"
      >
        <span
          v-if="sending"
          class="spinner-border spinner-border-sm me-1"
        ></span>
        {{ sending ? 'Envoi…' : 'Envoyer le mot de passe' }}
      </button>
    </footer>
  </BModal>
</template>

<style scoped>
.temp-password__lead {
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.6;
  color: #475467;
}

.temp-password__target {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e9ebec;
  border-radius: 6px;
  background: #fbfcfe;
}

.temp-password__initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #eef4fd;
  color: #005bd0;
  font-size: 13px;
  font-weight: 600;
}

.temp-password__identity {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.temp-password__name {
  font-size: 13px;
  font-weight: 500;
  color: #101828;
}

/* Une adresse longue se coupe plutôt qu'elle n'élargit la fenêtre. */
.temp-password__email {
  font-size: 12px;
  color: #667085;
  word-break: break-all;
}

/* L'ambre des avertissements de la plateforme: même trio de couleurs que les
   encadrés des écrans d'authentification. */
.temp-password__notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 16px 0 0;
  padding: 12px 14px;
  border: 1px solid #ffe1a8;
  border-radius: 6px;
  background: #fff8e6;
  font-size: 13px;
  line-height: 1.6;
  color: #8a5a00;
}

.temp-password__notice i {
  flex: none;
  margin-top: 2px;
  font-size: 15px;
}
</style>
